import { getAddress, type Address, type PublicClient } from "viem";
import { tokenFactoryAbi } from "../../config/factoryAbi";
import type { Hex, HistoryPage, LaunchSummary } from "../launcher/types";

/** One decoded TokenCreated row from a range fetch. */
export interface RawTokenCreated {
  tokenAddress: Hex;
  creator: Hex;
  name: string;
  symbol: string;
  supplySmallestUnits: bigint;
  txHash: Hex;
  blockNumber: bigint;
  logIndex: number;
}

/** Abstract source so the scanner can be tested without a live chain. */
export interface HistorySource {
  headBlock(): Promise<bigint>;
  /** Fetch TokenCreated in [from, to]; may throw (RPC range/timeout/rate limit). */
  fetchRange(from: bigint, to: bigint): Promise<RawTokenCreated[]>;
}

export interface ScanOptions {
  fromBlock: bigint;
  creator?: Hex;
  /** Upper bound (inclusive) for this page; defaults to head - confirmations. */
  beforeBlock?: bigint;
  /** Block window per request. Default 5000. */
  chunkSize?: bigint;
  /** Confirmations to wait before treating recent events as final. Default 5. */
  confirmations?: bigint;
  /** Chunks scanned per page (bounds a single load-more). Default 20. */
  maxChunks?: number;
  /** Retry attempts per failed chunk. Default 2. */
  retries?: number;
  /** Cap on rows returned per page. Default 100. */
  limit?: number;
}

export interface ScanResult {
  items: LaunchSummary[];
  /** True when one or more chunks failed after retries (results incomplete). */
  partial: boolean;
  /** Chunks that could not be read. */
  failedRanges: Array<{ from: string; to: string }>;
  /** Upper bound for the next older page, or null when the range is exhausted. */
  nextCursor: bigint | null;
  confirmations: number;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Scan newest-first in bounded windows with per-chunk retry, a confirmation
 * threshold, and duplicate suppression (txHash + logIndex). Reorg note: events
 * within `confirmations` of head are excluded; a deeper reorg could still change
 * history and is out of scope for a read-only view.
 */
export async function scanHistory(source: HistorySource, opts: ScanOptions): Promise<ScanResult> {
  const chunk = opts.chunkSize ?? 5000n;
  const confirmations = opts.confirmations ?? 5n;
  const maxChunks = opts.maxChunks ?? 20;
  const retries = opts.retries ?? 2;
  const limit = opts.limit ?? 100;
  const confNum = Number(confirmations);

  const head = await source.headBlock();
  const safeHead = head > confirmations ? head - confirmations : 0n;
  const start = opts.beforeBlock !== undefined && opts.beforeBlock < safeHead ? opts.beforeBlock : safeHead;
  if (start < opts.fromBlock) {
    return { items: [], partial: false, failedRanges: [], nextCursor: null, confirmations: confNum };
  }

  const seen = new Set<string>();
  const rows: RawTokenCreated[] = [];
  const failedRanges: ScanResult["failedRanges"] = [];
  let to = start;
  let chunks = 0;
  let nextCursor: bigint | null = null;

  while (to >= opts.fromBlock) {
    if (chunks >= maxChunks || rows.length >= limit) {
      nextCursor = to; // more to scan on a subsequent page
      break;
    }
    const from = to - chunk + 1n > opts.fromBlock ? to - chunk + 1n : opts.fromBlock;
    let got: RawTokenCreated[] | null = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        got = await source.fetchRange(from, to);
        break;
      } catch {
        if (attempt < retries) await sleep(50 * (attempt + 1));
      }
    }
    if (got === null) {
      failedRanges.push({ from: from.toString(), to: to.toString() });
    } else {
      for (const r of got) {
        if (opts.creator && getAddress(r.creator) !== getAddress(opts.creator)) continue;
        const id = `${r.txHash}:${r.logIndex}`;
        if (seen.has(id)) continue;
        seen.add(id);
        rows.push(r);
      }
    }
    if (from === opts.fromBlock) break;
    to = from - 1n;
    chunks++;
  }

  rows.sort((a, b) =>
    b.blockNumber === a.blockNumber
      ? b.logIndex - a.logIndex
      : b.blockNumber > a.blockNumber
        ? 1
        : -1
  );

  const items: LaunchSummary[] = rows.slice(0, limit).map((r) => ({
    tokenAddress: r.tokenAddress,
    creator: r.creator,
    name: r.name,
    symbol: r.symbol,
    supplySmallestUnits: r.supplySmallestUnits,
    txHash: r.txHash,
    blockNumber: r.blockNumber,
    logIndex: r.logIndex,
  }));

  return {
    items,
    partial: failedRanges.length > 0,
    failedRanges,
    nextCursor,
    confirmations: confNum,
  };
}

/** Build a viem-backed source and scan. Used by the real launcher client. */
export function viemHistorySource(
  client: PublicClient,
  factory: Hex,
  creator?: Hex
): HistorySource {
  return {
    headBlock: () => client.getBlockNumber(),
    async fetchRange(from, to) {
      // Canonical ETH-v1 factory emits MarketCreated (market, token, creator,
      // name, symbol, recipientCount). Total supply is fixed at 1e27 (1B × 1e18).
      const logs = await client.getContractEvents({
        address: factory as Address,
        abi: tokenFactoryAbi,
        eventName: "MarketCreated",
        ...(creator ? { args: { creator: creator as Address } } : {}),
        fromBlock: from,
        toBlock: to,
      });
      return logs.map((l) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const a = (l as any).args;
        return {
          tokenAddress: a.token as Hex,
          creator: a.creator as Hex,
          name: a.name as string,
          symbol: a.symbol as string,
          supplySmallestUnits: 1_000_000_000n * 10n ** 18n,
          txHash: l.transactionHash as Hex,
          blockNumber: l.blockNumber as bigint,
          logIndex: l.logIndex as number,
        };
      });
    },
  };
}

export async function scanTokenCreated(
  client: PublicClient,
  opts: { factory: Hex; creator?: Hex; fromBlock: bigint } & Partial<ScanOptions>
): Promise<HistoryPage> {
  const source = viemHistorySource(client, opts.factory, opts.creator);
  const { items, partial, failedRanges, nextCursor, confirmations } = await scanHistory(source, opts);
  return { items, partial, failedRanges, nextCursor, confirmations };
}
